import { useTranslation } from 'react-i18next';
import { PauseCircle } from 'lucide-react';
import useOrdersPaused from '@/hooks/useOrdersPaused';

/**
 * Merchant Orders Pause — tells the merchant WHY every create button is greyed
 * out.
 *
 * Without this, a paused account is a silent dead end: the buttons are disabled,
 * nothing explains it, and the merchant either files a ticket or assumes the
 * dashboard is broken. That exact failure has been found twice by QA on other
 * features, so the explanation ships with the block, not after it.
 *
 * Built to the house card language (see CurrentSlotBanner): white `rounded-2xl`
 * card on the rgb(232,230,227) hairline border, a 48px tinted icon tile, an
 * uppercase eyebrow, an extrabold headline and a muted supporting line. An
 * out-of-the-box Ant `<Alert>` reads as a framework default next to those and
 * was replaced.
 *
 * Amber, not red: the account is healthy and every other capability works. Red
 * would say "something is broken" and send the merchant to support for the
 * wrong reason. The tint sits on the icon tile and the footer only — the card
 * itself stays neutral, matching the "the strip alone carries the warning" rule
 * the slot banner already follows.
 *
 * Deliberately not a modal and deliberately not dismissible: a paused merchant
 * still has real work on this page — resolving on-hold orders, cancelling,
 * chasing remittances — and must not have to dismiss anything to reach it. The
 * footer names what still works so the block reads as a narrow restriction
 * rather than an outage.
 *
 * Renders nothing at all when the account is accepting orders, so it is safe to
 * mount unconditionally.
 */
const OrdersPausedBanner = () => {
  const { t } = useTranslation();
  const ordersPaused = useOrdersPaused();

  if (!ordersPaused) return null;

  const stillWorks = [
    t('edit'),
    t('cancel'),
    t('resolve'),
    t('return_to_origin'),
    t('remittances'),
  ];

  return (
    <div
      data-testid='orders-paused-banner'
      role='status'
      className='overflow-hidden rounded-2xl border border-solid'
      style={{ borderColor: 'rgb(232,230,227)', background: '#fff' }}
    >
      <div className='flex items-start gap-4 p-4'>
        <span
          className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl'
          style={{ background: 'rgba(245,158,11,0.12)' }}
        >
          <PauseCircle size={24} strokeWidth={1.8} color='rgb(180,83,9)' />
        </span>

        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-[11px] font-semibold uppercase tracking-wider text-[rgb(115,123,140)]'>
              {String(t('orders'))}
            </span>
            <span
              className='inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold'
              style={{ background: 'rgba(245,158,11,0.14)', color: 'rgb(146,64,14)' }}
            >
              {String(t('orders_paused_pill'))}
            </span>
          </div>

          {/* mb-0 / mt-0 are load-bearing: Ant's reset gives headings and
              paragraphs their own margins, which opened a ~30px gap between
              these two lines and made the card read as two unrelated blocks. */}
          <h3 className='mb-0 mt-1 text-xl font-extrabold tracking-tight'>
            {String(t('orders_paused_title'))}
          </h3>

          <p className='mb-0 mt-1 text-sm font-medium leading-relaxed text-[rgb(115,123,140)]'>
            {String(t('orders_paused_banner'))}
          </p>
        </div>
      </div>

      <div
        className='flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-solid px-4 py-3'
        style={{ borderColor: 'rgb(240,239,236)', background: 'rgb(250,250,249)' }}
      >
        <span className='text-[11px] font-semibold uppercase tracking-wider text-[rgb(115,123,140)]'>
          {String(t('orders_paused_still_available'))}
        </span>
        {stillWorks.map((label) => (
          <span
            key={String(label)}
            className='inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold capitalize'
            style={{
              background: '#fff',
              border: '1px solid rgb(232,230,227)',
              color: 'rgb(57,57,60)',
            }}
          >
            {String(label)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default OrdersPausedBanner;
